import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { Store } from '../types';

// ── Default store list (mirrored from worker registry in src/worker/index.ts) ──
// Note: CCS uses q0gtxj-s0.myshopify.com (actual Shopify domain) since D1 records use that.
const DEFAULT_STORES: Store[] = [
  { shop: '', domain: '', label: 'All Stores', shortCode: 'ALL' },
  { shop: 'q0gtxj-s0.myshopify.com', domain: 'casualchicshop.com', label: 'Casual Chic Shop', shortCode: 'CCS' },
  { shop: 'casual-chic-boutique-2.myshopify.com', domain: 'casualchicboutique.com', label: 'Casual Chic Boutique', shortCode: 'CCB' },
  { shop: 'casual-chic-luxe.myshopify.com', domain: 'casualchicluxe.com', label: 'Casual Chic Luxe', shortCode: 'CCL' },
  { shop: 'casual-chic-collection.myshopify.com', domain: 'casualchiccollection.com', label: 'Casual Chic Collection', shortCode: 'CCC' },
  { shop: 'the-bargains-depot.myshopify.com', domain: 'thebargainsdepot.com', label: 'The Bargains Depot', shortCode: 'TBD' },
];

interface StoreContextValue {
  stores: Store[];
  activeStore: Store;
  setActiveStore: (store: Store) => void;
  switchStore: (shopDomain: string) => void;
}

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [stores] = useState<Store[]>(DEFAULT_STORES);
  const [activeStore, setActiveStore] = useState<Store>(DEFAULT_STORES[0]);

  const switchStore = useCallback(
    (shopDomain: string) => {
      const found = stores.find((s) => s.shop === shopDomain || s.domain === shopDomain);
      if (found) setActiveStore(found);
    },
    [stores],
  );

  return (
    <StoreContext.Provider value={{ stores, activeStore, setActiveStore, switchStore }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within <StoreProvider>');
  return ctx;
}
